import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import useChatEvents from './useChatEvents';

/**
 * useWitnessCleanup — the run lifecycle for the bounded witness cleanup workflow
 * (drawer.witness_refine), owned by the roster so Arc proposes *into* the tab
 * instead of over it in a modal. Seam consolidation step B
 * (WITNESS_CLEANUP_WORKFLOW_PLAN §12 #5).
 *
 * Lifecycle: idle → start(subset) → /agent/run → poll events → plan_ready
 * (ready) → apply() → /agent/resume {approved_item_ids} → apply_complete (done)
 * → dismiss() → idle. Because the hook lives in the roster (not a mounted
 * modal), a run keeps going while the user works elsewhere in the tab — the
 * inline lane is the surface, no minimize-to-chip needed.
 *
 * `start` takes the raw witness subset and builds the `refine_targets` scope.
 * `onApplied(result)` fires after apply_complete and after a result-card undo so
 * the host can reload the roster + recently-merged ledger.
 */
const PLAN_TIMEOUT_MS = 180_000;
const APPLY_TIMEOUT_MS = 240_000;

export default function useWitnessCleanup(caseId, { onApplied } = {}) {
  const { token } = useAuth();

  const [phase, setPhase] = useState('idle'); // idle|starting|running|ready|applying|done|empty|error
  const [targets, setTargets] = useState(null); // the witness subset in scope
  const [conversationId, setConversationId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [rejectedIds, setRejectedIds] = useState(() => new Set()); // rows the human UNchecked
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [undoneOps, setUndoneOps] = useState(() => new Set()); // op_ids undone in-place
  const [undoingOp, setUndoingOp] = useState(null);             // op_id with an undo in flight

  const seenSeqsRef = useRef(new Set());
  const phaseRef = useRef(phase);
  const watchdogRef = useRef(null);
  // handleEvents fires from the polling closure with stale state — the
  // auto-apply path reads these refs instead.
  const conversationIdRef = useRef(null);
  const applyIdsRef = useRef(null);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current) { clearInterval(watchdogRef.current); watchdogRef.current = null; }
  }, []);
  const startWatchdog = useCallback((ms, msg) => {
    stopWatchdog();
    const deadline = Date.now() + ms;
    watchdogRef.current = setInterval(() => {
      if (Date.now() > deadline) { stopWatchdog(); setError(msg); setPhase('error'); }
    }, 3000);
  }, [stopWatchdog]);

  const handleEvents = useCallback((events) => {
    for (const evt of events) {
      if (evt.sequence != null) {
        if (seenSeqsRef.current.has(evt.sequence)) continue;
        seenSeqsRef.current.add(evt.sequence);
      }
      const t = evt.event_type || evt.type;
      const d = evt.data ?? evt;

      if (t === 'progress' || t === 'tool_progress') {
        if (d.message) setProgress(d.message);
      } else if (t === 'plan_ready' || t === 'skill_paused') {
        stopWatchdog();
        const ops = d.ops || [];
        // The backend-stamped default encodes the dial-scaled verify gate:
        // verified merges arrive checked, ⚠ unconfirmed ones unchecked.
        const rejected = new Set(ops
          .filter((o) => (o.default_checked != null
            ? o.default_checked === false
            : o.verify && !o.verify.held))
          .map((o) => o.id));
        const approved = ops.filter((o) => !rejected.has(o.id)).map((o) => o.id);
        setPlan({ summary: d.summary || '', ops });
        setRejectedIds(rejected);
        if (!ops.length) {
          setPhase('empty');
        } else if (approved.length > 0) {
          // Invisible Resolution (E2 follow-up): verified ops apply
          // IMMEDIATELY — every merge is reversible (per-row Undo on the
          // result card), so an approval step was a speed bump, not a
          // safety layer. Only unverified leftovers wait for a human.
          applyIdsRef.current?.(approved);
        } else {
          setPhase('ready'); // every op unverified — human opt-in stays
        }
      } else if (t === 'apply_complete') {
        stopWatchdog();
        setResult(d);
        setPhase('done');
        onApplied?.(d);
      } else if (t === 'plan_error' || t === 'error' || t === 'tool_error') {
        stopWatchdog();
        setError(d.message || d.error || 'Arc hit an error.');
        setPhase('error');
        return;
      } else if (t === 'plan_budget_exceeded') {
        stopWatchdog();
        setError('This took longer than the allotted budget. Try a smaller subset.');
        setPhase('error');
        return;
      }
    }
  }, [onApplied, stopWatchdog]);

  const { start: startEvents, stop: stopEvents, getSeq } = useChatEvents(
    caseId, conversationId, { getToken: () => token, onEvents: handleEvents, intervalMs: 750 },
  );

  useEffect(() => () => { stopEvents(); stopWatchdog(); }, [stopEvents, stopWatchdog]);

  const start = useCallback(async (subset, aggressiveness) => {
    const list = (subset || []).filter(Boolean);
    if (list.length === 0) return;
    const refineTargets = list.map((w) => ({
      witness_id: w.id,
      display_name: w.display_name,
      entity_ids: w.member_entity_ids || [],
    }));
    seenSeqsRef.current = new Set();
    setTargets(list);
    setPlan(null); setResult(null); setError('');
    setRejectedIds(new Set()); setUndoneOps(new Set()); setUndoingOp(null);
    setProgress(`Arc is reviewing ${list.length} witness${list.length === 1 ? '' : 'es'}…`);
    setPhase('starting');
    try {
      const res = await api.post('/agent/run', {
        surface_id: 'drawer.witness_refine',
        case_id: caseId,
        // Always the calibrated policy — the learned zero-false-merge band.
        // The aggressiveness dial is retired everywhere (gold-calibration
        // plan); witness_refine's guidance has a calibrated entry.
        scope: { refine_targets: refineTargets, aggressiveness: aggressiveness || 'calibrated' },
      });
      conversationIdRef.current = res.conversation_id;
      setConversationId(res.conversation_id);
      setPhase('running');
      startEvents(0, res.conversation_id);
      startWatchdog(
        PLAN_TIMEOUT_MS,
        list.length <= 3
          ? 'Arc took too long to review. If these are clearly the same person, use “Merge into…” on one of them instead.'
          : 'Arc took too long to review. Narrow the batch with filters and try again.',
      );
    } catch (e) {
      setError(e.message || 'Failed to start cleanup.');
      setPhase('error');
    }
  }, [caseId, startEvents, startWatchdog]);

  const approvedIds = useMemo(
    () => (plan?.ops || []).filter((o) => !rejectedIds.has(o.id)).map((o) => o.id),
    [plan, rejectedIds],
  );
  const toggleRow = useCallback((id) => setRejectedIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const applyIds = useCallback(async (ids) => {
    const cid = conversationIdRef.current;
    if (!cid || !ids?.length) return;
    setPhase('applying');
    setProgress('Applying the verified changes…');
    try {
      await api.post('/agent/resume', {
        conversation_id: cid,
        case_id: caseId,
        user_input: { action: 'approve', approved_item_ids: ids },
      });
      startEvents(getSeq(), cid);
      startWatchdog(APPLY_TIMEOUT_MS, 'Apply took too long. Refresh the roster to see what landed.');
    } catch (e) {
      setError(e.message || 'Failed to apply.');
      setPhase('ready');
    }
  }, [caseId, startEvents, getSeq, startWatchdog]);
  useEffect(() => { applyIdsRef.current = applyIds; }, [applyIds]);
  // Manual apply — only reachable from the all-unverified fallback lane.
  const apply = useCallback(() => applyIds(approvedIds), [applyIds, approvedIds]);

  // Undo a just-applied merge from the result card — reverses its recorded
  // merge ops via the same ledger as Recently-merged, then asks the host to
  // reload so the restore shows.
  const undo = useCallback(async (op) => {
    if (!op?.merge_op_ids?.length || undoingOp) return;
    setUndoingOp(op.op_id);
    try {
      await api.post(`/cases/${caseId}/witnesses/merges/undo`, {
        op_ids: op.merge_op_ids,
      });
      setUndoneOps((s) => new Set(s).add(op.op_id));
      onApplied?.(result);
    } catch (e) {
      setError(e.message || 'Undo failed.');
    } finally {
      setUndoingOp(null);
    }
  }, [caseId, undoingOp, onApplied, result]);

  const dismiss = useCallback(() => {
    stopEvents(); stopWatchdog();
    conversationIdRef.current = null;
    setPhase('idle'); setTargets(null); setConversationId(null);
    setPlan(null); setResult(null); setError(''); setProgress('');
    setRejectedIds(new Set()); setUndoneOps(new Set()); setUndoingOp(null);
    seenSeqsRef.current = new Set();
  }, [stopEvents, stopWatchdog]);

  // Cancel a still-running agent (best-effort) and return to idle.
  const cancel = useCallback(async () => {
    if (conversationId && ['starting', 'running', 'ready', 'applying'].includes(phaseRef.current)) {
      try {
        await api.post('/agent/resume', {
          conversation_id: conversationId, case_id: caseId, user_input: 'cancel',
        });
      } catch { /* best-effort */ }
    }
    dismiss();
  }, [conversationId, caseId, dismiss]);

  // A run that hasn't reached a terminal state — used to disable re-entry.
  const active = !['idle', 'done', 'empty', 'error'].includes(phase);

  return {
    phase, targets, plan, progress, result, error,
    rejectedIds, approvedIds, undoneOps, undoingOp, active,
    start, toggleRow, apply, undo, cancel, dismiss,
  };
}
